const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_IN_TEXT_RE =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export function isNotificationUuid(value: string): boolean {
  return UUID_RE.test(value.trim());
}

/** Extract UUIDs from refTableId (supports composite `"<ref-id>, <user-task-id>"`). */
export function parseRefTableIdUuids(refTableId: string): string[] {
  const trimmed = (refTableId ?? '').trim();
  if (!trimmed) return [];
  // Clone the global regex so lastIndex cannot leak across calls.
  return trimmed.match(new RegExp(UUID_IN_TEXT_RE.source, 'gi')) ?? [];
}

/**
 * User task id from refTableId.
 * When composite, the second UUID is the workflow instance stage user task id.
 */
export function parseUserTaskIdFromRefTableId(refTableId: string): string | undefined {
  const uuids = parseRefTableIdUuids(refTableId);
  if (uuids.length >= 2) return uuids[1];
  if (uuids.length === 1) return uuids[0];
  return undefined;
}

/** Whether the notification should open Task/Case Details (CMS Notifications screen). */
export function isTaskRelevantNotification(input: {
  caseRequestId?: string;
  workflowTaskId?: string;
  refTableName?: string;
  refTableId?: string;
  notificationTypeCode?: string;
  scenario?: string;
}): boolean {
  const refTable = (input.refTableName ?? '').trim().toLowerCase();
  const refUuids =
    refTable === 'workflow_instance_stage_user_task' || refTable === 'case_request'
      ? parseRefTableIdUuids(input.refTableId ?? '')
      : [];
  const workflowTaskId =
    (input.workflowTaskId ?? '').trim() ||
    (refTable === 'workflow_instance_stage_user_task'
      ? refUuids[0] || refUuids[1] || ''
      : '') ||
    (refTable === 'case_request' && refUuids.length >= 2 ? (refUuids[1] ?? '') : '');

  // SLA / task notifications may omit caseRequestId but still include a user task id.
  if (isNotificationUuid(workflowTaskId)) return true;

  const caseRequestId = (input.caseRequestId ?? '').trim();
  if (!isNotificationUuid(caseRequestId)) return false;

  const typeHay = `${input.notificationTypeCode ?? ''} ${input.scenario ?? ''}`.toUpperCase();
  if (isLegalOutcomeNotification(input)) return false;
  return /(TASK|CASE|ASSIGN|SUBMIT|ESCAL|FORWARD|REVERT|CLOSURE|WORKFLOW|SLA|STATUS)/.test(typeHay);
}

/** Resolve user task id from notification payload fields. */
export function resolveNotificationUserTaskId(input: {
  workflowTaskId?: string;
  refTableName?: string;
  refTableId?: string;
}): string {
  const refTable = (input.refTableName ?? '').trim().toLowerCase();
  const refUuids = parseRefTableIdUuids(input.refTableId ?? '');

  // Composite refs: second UUID is the workflow instance stage user task id.
  if (refTable === 'workflow_instance_stage_user_task' && refUuids.length >= 2) {
    const fromComposite = refUuids[1] ?? '';
    if (isNotificationUuid(fromComposite)) return fromComposite;
  }
  if (refTable === 'case_request' && refUuids.length >= 2) {
    const fromComposite = refUuids[1] ?? '';
    if (isNotificationUuid(fromComposite)) return fromComposite;
  }

  const workflowTaskId = (input.workflowTaskId ?? '').trim();
  if (isNotificationUuid(workflowTaskId)) return workflowTaskId;

  if (refTable === 'workflow_instance_stage_user_task') {
    const fromRef = parseUserTaskIdFromRefTableId(input.refTableId ?? '') || '';
    if (isNotificationUuid(fromRef)) return fromRef;
  }

  return '';
}

/** Legal Knowledge Archive — outcome ready for review/archiving. */
export function isLegalOutcomeNotification(input: {
  notificationTypeCode?: string;
  scenario?: string;
}): boolean {
  const type = (input.notificationTypeCode ?? '').trim().toUpperCase();
  const scenario = (input.scenario ?? '').trim().toUpperCase();
  return type === 'LEGAL_OUTCOME' || scenario === 'LEGAL_OUTCOME_EXTRACTED';
}

/** Router state when opening a legal outcome from the notifications screen. */
export type LegalOutcomeNotificationNavigationState = {
  fromLegalOutcomeNotification?: boolean;
};

/** Extracted case outcome review — `/legal-knowledge/archive/management/outcomes/:id`. */
export function buildLegalOutcomeNotificationPath(input: {
  extractedCaseOutcomeId?: string;
  caseRequestId?: string;
  status?: string;
}): string | undefined {
  const extractedCaseOutcomeId = (input.extractedCaseOutcomeId ?? '').trim();
  if (!isNotificationUuid(extractedCaseOutcomeId)) return undefined;

  const path = `/legal-knowledge/archive/management/outcomes/${encodeURIComponent(extractedCaseOutcomeId)}`;
  const caseRequestId = (input.caseRequestId ?? '').trim();
  if (!isNotificationUuid(caseRequestId)) return path;

  const qs = new URLSearchParams({
    caseRequestId,
    status: (input.status ?? 'DRAFT').trim() || 'DRAFT',
  });
  return `${path}?${qs.toString()}`;
}

/** Build Task/Case Details route from notification payload fields. */
export function buildTaskCaseDetailsPathFromNotification(input: {
  caseRequestId?: string;
  workflowTaskId?: string;
  refTableName?: string;
  refTableId?: string;
}): string | undefined {
  const caseRequestId = (input.caseRequestId ?? '').trim();
  if (!isNotificationUuid(caseRequestId)) return undefined;

  const base = `/tasks/${encodeURIComponent(caseRequestId)}`;
  const userTaskId = resolveNotificationUserTaskId(input);

  if (userTaskId) {
    const qs = new URLSearchParams({ userTaskId });
    return `${base}?${qs.toString()}`;
  }
  return base;
}

/**
 * Ensure a `/tasks/...` path includes the correct `userTaskId` query param.
 * Overwrites a missing or incorrect userTaskId when `resolvedUserTaskId` is known.
 */
export function ensureTaskPathUserTaskId(path: string, resolvedUserTaskId: string): string {
  const userTaskId = resolvedUserTaskId.trim();
  if (!userTaskId || !isNotificationUuid(userTaskId)) return path;

  const trimmed = path.trim();
  if (!trimmed) return path;

  try {
    const abs = /^https?:\/\//i.test(trimmed);
    const url = abs ? new URL(trimmed) : new URL(trimmed, 'http://local');
    if (!url.pathname.includes('/tasks/')) return path;

    url.searchParams.set('userTaskId', userTaskId);
    if (abs) return `${url.origin}${url.pathname}${url.search}${url.hash}`;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return path;
  }
}

/**
 * Prefer a fully-qualified task path (case + user task) built from notification fields
 * over an incomplete API action URL.
 */
export function resolveTaskNotificationPath(input: {
  actionUrlFromApi?: string;
  caseRequestId?: string;
  workflowTaskId?: string;
  refTableName?: string;
  refTableId?: string;
}): string | undefined {
  const built = buildTaskCaseDetailsPathFromNotification(input);
  const userTaskId = resolveNotificationUserTaskId(input);
  const apiUrl = (input.actionUrlFromApi ?? '').trim();

  // Complete built path always wins when we know the user task id.
  if (built && userTaskId) return built;

  if (apiUrl) {
    return userTaskId ? ensureTaskPathUserTaskId(apiUrl, userTaskId) : apiUrl;
  }

  return built;
}
